import React, { useState } from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';

interface CreateRoomModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (name: string, description: string, icon: string) => Promise<void>;
}

const ModalBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--bg-primary);
  backdrop-filter: blur(8px);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing.md};
`;

const ModalContent = styled.div`
  background: var(--bg-card);
  width: 100%;
  max-width: 480px;
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing.xl};
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`;

const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;

const FieldLabel = styled.label`
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const TextInput = styled.input`
  width: 100%;
  padding: ${theme.spacing.md};
  border: 1px solid var(--border-medium);
  border-radius: ${theme.borderRadius.md};
  background: var(--bg-card);
  color: var(--text-primary);
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 90px;
  padding: ${theme.spacing.md};
  border: 1px solid var(--border-medium);
  border-radius: ${theme.borderRadius.md};
  background: var(--bg-card);
  color: var(--text-primary);
  font-size: 1rem;
  font-family: inherit;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

const IconRow = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
`;

const IconOption = styled.button<{ $selected: boolean }>`
  width: 44px;
  height: 44px;
  font-size: 1.3rem;
  border-radius: ${theme.borderRadius.md};
  border: 2px solid ${props => props.$selected ? theme.colors.primary : 'var(--border-medium)'};
  background: ${props => props.$selected ? `${theme.colors.primary}15` : 'var(--bg-card)'};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${theme.colors.primary};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  justify-content: flex-end;
  margin-top: ${theme.spacing.sm};
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.md};
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => props.$variant === 'primary' ? `
    background: ${theme.colors.primary};
    color: white;
    border: none;

    &:hover:not(:disabled) {
      background: ${theme.colors.hover.primary};
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  ` : `
    background: transparent;
    color: var(--text-primary);
    border: 1px solid var(--border-medium);

    &:hover {
      background: var(--bg-secondary);
    }
  `}
`;

const ICON_OPTIONS = ['💬', '❤️', '✈️', '💪', '🎮', '🎵', '📚', '🍔', '🎬', '⚽'];

export const CreateRoomModal: React.FC<CreateRoomModalProps> = ({ isOpen, onClose, onCreate }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState(ICON_OPTIONS[0]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleCreate = async () => {
    if (!name.trim()) return;
    setIsSubmitting(true);
    try {
      await onCreate(name.trim(), description.trim(), icon);
      setName('');
      setDescription('');
      setIcon(ICON_OPTIONS[0]);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalBackdrop>
      <ModalContent>
        <Title>Create Chat Room</Title>

        <FormGroup>
          <FieldLabel htmlFor="room-name">Room Name</FieldLabel>
          <TextInput
            id="room-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Weekend Hikers"
            maxLength={100}
            autoFocus
          />
        </FormGroup>

        <FormGroup>
          <FieldLabel htmlFor="room-description">Description</FieldLabel>
          <TextArea
            id="room-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What's this room about?"
            maxLength={300}
          />
        </FormGroup>

        <FormGroup>
          <FieldLabel>Icon</FieldLabel>
          <IconRow>
            {ICON_OPTIONS.map((option) => (
              <IconOption
                key={option}
                type="button"
                $selected={icon === option}
                onClick={() => setIcon(option)}
              >
                {option}
              </IconOption>
            ))}
          </IconRow>
        </FormGroup>

        <ButtonGroup>
          <Button type="button" onClick={onClose}>Cancel</Button>
          <Button $variant="primary" onClick={handleCreate} disabled={isSubmitting || !name.trim()}>
            {isSubmitting ? 'Creating...' : 'Create Room'}
          </Button>
        </ButtonGroup>
      </ModalContent>
    </ModalBackdrop>
  );
};
